/**
 * Card capture (TMMAPP-0015): bearer client-config → Stripe.js Elements → tokenize.
 * The only seam that produces a `cardToken`. Never logs or stores it beyond `onToken`.
 *
 * TMMAPP-0119: the card is three outlined fields side by side — number (flex 2), expiry and CVC
 * (flex 1 each), wrapping to two rows at `xs` (item 22) — then the billing address (Stripe's Address
 * Element, §B), then the host's children (Promo code), with no reserved height (item 21). A card
 * error is one catalog sentence under the fields, tied by `aria-describedby` (item 20).
 */
import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Alert, Box, Button, FormHelperText, InputLabel, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { SubmitButton } from '@/components/SubmitButton';
import { resolveTokens } from '@/theme';
import type { CardElementTargets, CardField } from './stripeSdk';
import { useCardToken, type CardFieldState, type TokenizeResult } from './useCardToken';

export interface CardCaptureHandle {
  tokenize: () => Promise<TokenizeResult>;
  retry: () => void;
}

export interface CardCaptureProps {
  onToken?: (token: string) => void | Promise<void | 'retry'>;
  submitLabel?: string;
  /** Show "Charges appear on your card statement as …" under the card when served (item 12). */
  showStatementDescriptor?: boolean;
  /** When true, the built-in submit button is omitted (wizard bottom bar owns LET'S GO). */
  hideSubmit?: boolean;
  /** Notifies the parent when the three card fields report complete (for enabling LET'S GO). */
  onCompleteChange?: (complete: boolean) => void;
  /** The host's own visible "Card" heading; without one, the fields carry their own label. */
  labelledBy?: string;
  busy?: boolean;
  /**
   * The built-in submit is in flight, from the press through tokenize and `onToken` (TMMAPP-0122
   * item 25): the host reports busy from the start, so its close is held too.
   */
  onSubmittingChange?: (submitting: boolean) => void;
  children?: ReactNode;
}

const FIELDS: ReadonlyArray<{
  field: CardField;
  labelKey: string;
  flex: { xs: string; sm: string };
}> = [
  { field: 'number', labelKey: 'card.fields.number', flex: { xs: '1 1 100%', sm: '2 1 0' } },
  { field: 'expiry', labelKey: 'card.fields.expiry', flex: { xs: '1 1 0', sm: '1 1 0' } },
  { field: 'cvc', labelKey: 'card.fields.cvc', flex: { xs: '1 1 0', sm: '1 1 0' } },
];

export const CardCapture = forwardRef<CardCaptureHandle, CardCaptureProps>(function CardCapture(
  {
    onToken,
    submitLabel,
    hideSubmit = false,
    showStatementDescriptor = false,
    onCompleteChange,
    labelledBy,
    busy = false,
    onSubmittingChange,
    children,
  },
  ref,
) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const ownLabelId = useId();
  const errorId = useId();
  const numberRef = useRef<HTMLDivElement | null>(null);
  const expiryRef = useRef<HTMLDivElement | null>(null);
  const cvcRef = useRef<HTMLDivElement | null>(null);
  const addressRef = useRef<HTMLDivElement | null>(null);
  // The mount targets, read once the containers are in the DOM (the hook mounts after commit).
  const targets = useMemo(
    () => ({
      get current(): CardElementTargets | null {
        const number = numberRef.current;
        const expiry = expiryRef.current;
        const cvc = cvcRef.current;
        const address = addressRef.current;
        return number && expiry && cvc && address ? { number, expiry, cvc, address } : null;
      },
    }),
    [],
  );
  const { status, complete, fields, fieldMessage, statementDescriptor, tokenize, retry } =
    useCardToken(targets);
  const [cardError, setCardError] = useState<string | null>(null);
  // Set before tokenize: a second press while one is in flight sends nothing (item 25).
  const submitting = useRef(false);
  const onCompleteRef = useRef(onCompleteChange);
  const fieldRefs: Record<CardField, typeof numberRef> = {
    number: numberRef,
    expiry: expiryRef,
    cvc: cvcRef,
  };

  useImperativeHandle(ref, () => ({ tokenize, retry }), [tokenize, retry]);

  useEffect(() => {
    onCompleteRef.current = onCompleteChange;
  });

  useEffect(() => {
    onCompleteRef.current?.(complete);
  }, [complete]);

  async function runToken(result: TokenizeResult): Promise<void | 'retry'> {
    if (result.ok) {
      if (!onToken) return undefined;
      return onToken(result.token);
    }
    // A field refusal already shows under the fields.
    if (result.fieldError !== true) {
      setCardError(result.message ?? t('card.declined.INVALID_TOKEN'));
    }
    return undefined;
  }

  async function handleSubmit() {
    if (busy || submitting.current || !onToken) return;
    submitting.current = true;
    onSubmittingChange?.(true);
    setCardError(null);
    try {
      const first = await tokenize();
      const outcome = await runToken(first);
      if (outcome !== 'retry') return;
      const second = await tokenize();
      await runToken(second);
    } finally {
      submitting.current = false;
      onSubmittingChange?.(false);
    }
  }

  const shown = status === 'ready' || status === 'loading';
  const shownError = status === 'ready' ? (fieldMessage ?? cardError) : null;

  return (
    <Stack spacing={2}>
      {status === 'loading' && <Typography variant="body2">{t('common.loading')}</Typography>}
      {status === 'notConfigured' && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={retry}>
              {t('common.retry')}
            </Button>
          }
        >
          {t('billing.notConfigured')}
        </Alert>
      )}
      {status === 'unavailable' && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={retry}>
              {t('common.retry')}
            </Button>
          }
        >
          {t('billing.unavailable')}
        </Alert>
      )}
      {status === 'unsupported' && (
        <Alert severity="warning">{t('billing.providerUnsupported')}</Alert>
      )}
      <Stack id="card-container" spacing={2} sx={{ display: shown ? 'flex' : 'none' }}>
        <Box
          role="group"
          aria-labelledby={labelledBy ?? ownLabelId}
          aria-describedby={shownError !== null ? errorId : undefined}
          data-card-fields=""
        >
          {labelledBy === undefined && (
            <InputLabel component="div" id={ownLabelId}>
              {t('card.fields.label')}
            </InputLabel>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {FIELDS.map(({ field, labelKey, flex }) => (
              <CardFieldBox
                key={field}
                field={field}
                label={t(labelKey)}
                state={fields}
                flex={flex}
                lineStrong={tokens.lineStrong}
                focusRing={tokens.focusRing}
                errorFg={tokens.errorFg}
                radius={tokens.radius.control}
                mountRef={fieldRefs[field]}
              />
            ))}
          </Box>
          {shownError !== null && (
            <FormHelperText error id={errorId} data-card-error="">
              {shownError}
            </FormHelperText>
          )}
        </Box>
        <Box>
          <InputLabel component="div">{t('card.billingAddress')}</InputLabel>
          <Box ref={addressRef} data-card-address="" />
        </Box>
      </Stack>
      {showStatementDescriptor && status === 'ready' && statementDescriptor !== null && (
        <Typography variant="body2" data-statement-descriptor="" sx={{ color: 'text.secondary' }}>
          {t('billing.statementDescriptor', { descriptor: statementDescriptor })}
        </Typography>
      )}
      {children}
      {status === 'ready' && !hideSubmit && submitLabel != null && (
        <Box>
          <SubmitButton loading={busy} disabled={!complete} onClick={() => void handleSubmit()}>
            {submitLabel}
          </SubmitButton>
        </Box>
      )}
    </Stack>
  );
});

/**
 * One outlined field around a Stripe Element (item 22): the app's outlined-field box — a 1px
 * `lineStrong` boundary at the control radius and the text field's padding — with the 2px
 * `focusRing` while Stripe reports focus and an `errorFg` border while it reports an error.
 */
function CardFieldBox({
  field,
  label,
  state,
  flex,
  lineStrong,
  focusRing,
  errorFg,
  radius,
  mountRef,
}: {
  field: CardField;
  label: string;
  state: CardFieldState;
  flex: { xs: string; sm: string };
  lineStrong: string;
  focusRing: string;
  errorFg: string;
  radius: number;
  mountRef: React.RefObject<HTMLDivElement | null>;
}) {
  const { focused, invalid } = state[field];
  const ring = invalid ? errorFg : focused ? focusRing : lineStrong;
  return (
    <Box
      role="group"
      aria-label={label}
      data-card-field={field}
      data-focused={focused ? 'true' : undefined}
      data-invalid={invalid ? 'true' : undefined}
      sx={{
        flex,
        minWidth: 0,
        border: `1px solid ${ring}`,
        // The 2px focus ring without a layout shift: the border plus a 1px inset shadow.
        boxShadow: focused ? `inset 0 0 0 1px ${ring}` : 'none',
        borderRadius: `${radius}px`,
        px: '12px',
        py: '10px',
      }}
    >
      <div ref={mountRef} style={{ minHeight: 21 }} />
    </Box>
  );
}
